import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  Button,
  Box,
  TextField,
  MenuItem,
  Alert,
  Grid,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import api from '../services/api';

const AppointmentModal = ({ open, onClose, onSkip }) => {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    date: '',
    time: '',
    message: '',
  });
  const [alert, setAlert] = useState({ show: false, message: '', type: 'success' });
  const [submitting, setSubmitting] = useState(false);

  const services = [
    'Legal Consultation',
    'Court Representation',
    'Document Review',
    'Contract Drafting',
    'Mediation Services',
    'Legal Research',
  ];

  const timeSlots = [
    '09:00 AM',
    '10:00 AM',
    '11:00 AM',
    '12:00 PM',
    '01:00 PM',
    '02:00 PM',
    '03:00 PM',
    '04:00 PM',
    '05:00 PM',
  ];

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const appointmentData = {
        ...formData,
        date: formData.date,
      };
      await api.post('/appointments', appointmentData);
      setAlert({
        show: true,
        message: 'Appointment request submitted successfully! We will confirm shortly.',
        type: 'success',
      });
      setFormData({
        name: '',
        email: '',
        phone: '',
        service: '',
        date: '',
        time: '',
        message: '',
      });
      // Close modal after 2 seconds on success
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      setAlert({
        show: true,
        message:
          error.response?.data?.message || 'Error submitting appointment. Please try again.',
        type: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSkip = () => {
    onSkip();
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        // Allow closing by clicking outside or ESC, but also call skip handler
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          handleSkip();
        }
      }}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          border: '1px solid #D4A574',
        },
      }}
    >
      <DialogTitle
        sx={{
          textAlign: 'center',
          pb: 2,
          borderBottom: '1px solid rgba(0,0,0,0.1)',
        }}
      >
        <Typography
          variant="h5"
          component="div"
          sx={{
            fontWeight: 600,
            fontFamily: theme.typography.fontFamily,
            color: theme.palette.primary.main,
          }}
        >
          Book an Appointment
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Schedule a consultation to discuss your legal needs (Optional)
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: 3, px: 4, pb: 2 }}>
        {alert.show && (
          <Alert
            severity={alert.type}
            sx={{ mb: 3 }}
            onClose={() => setAlert({ ...alert, show: false })}
          >
            {alert.message}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Full Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Service"
                name="service"
                value={formData.service}
                onChange={handleChange}
                required
                size="small"
              >
                {services.map((service) => (
                  <MenuItem key={service} value={service}>
                    {service}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Preferred Date"
                name="date"
                type="date"
                value={formData.date}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: new Date().toISOString().split('T')[0] }}
                required
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Preferred Time"
                name="time"
                value={formData.time}
                onChange={handleChange}
                required
                size="small"
              >
                {timeSlots.map((time) => (
                  <MenuItem key={time} value={time}>
                    {time}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Message (Optional)"
                name="message"
                value={formData.message}
                onChange={handleChange}
                multiline
                rows={3}
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  sx={{
                    backgroundColor: theme.palette.secondary.main,
                    color: 'white',
                    px: 4,
                    py: 1,
                    borderRadius: 2,
                    fontWeight: 600,
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: theme.palette.secondary.dark,
                    },
                  }}
                  disabled={submitting}
                >
                  {submitting ? 'Submitting...' : 'Submit Appointment'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleSkip}
                  sx={{
                    px: 4,
                    py: 1,
                    borderRadius: 2,
                    fontWeight: 600,
                    textTransform: 'none',
                    borderColor: theme.palette.text.secondary,
                    color: theme.palette.text.secondary,
                    '&:hover': {
                      borderColor: theme.palette.text.primary,
                      color: theme.palette.text.primary,
                    },
                  }}
                >
                  Skip for Now
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AppointmentModal;

